/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react';
import Dropdown from 'rc-dropdown';
import Menu, { Item as MenuItem } from 'rc-menu';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import 'rc-dropdown/assets/index.css';
import { useTheme } from 'emotion-theming';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { PACE_MENU_ITEMS, formatPaceLabel } from '../../utils/pacingPercentages';
import { FOCUS_FILL_VAR } from '../../../../shared/theme/focusStyles';
import { claimArrowKeys, hasArrowKeyOwner } from '../../utils/arrowKeyOwnership';

const DropdownIcon = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
`;

const DropdownContainerPrimary = styled('div')`
  display: inline-flex;
  align-items: center;
  ${(props) => props.visible && `
    cursor: pointer;
    /* Even padding around the label for the focus bubble (matches lifts);
       negative margin keeps layout unchanged. The table's scroll
       padding-left gives the ring room so it isn't clipped. */
    padding: 2px 4px;
    margin: -2px -4px;
    border-radius: 4px;
    &:focus { outline: none; }
    &:focus-visible {
      outline: none;
      background: ${FOCUS_FILL_VAR};
      box-shadow: inset 0 0 0 2px #BFDBFE;
    }
  `}
`;

const MenuWrapper = styled('div')`
  display: flex;
  flex-direction: column;
`;

const CustomDivider = styled.div`
  height: 1px;
  margin: 8px 4px;
  background: ${({ theme }) => theme.colors.borderLight};
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  /** No fill of its own: the MenuItem paints the whole row, so the highlight
   * and hover cover the full option instead of an inner box. */
  border-radius: 4px;
  padding: 5px;
`;

const DeleteIconContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  padding: 4px;
`;

const ArrowDiv = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

const MenuText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12pt;
  color: ${(props) => (props.color)};
  margin-bottom: 0px;
  font-size: 16px;
`;

const SwapPropDropdownSAQC = ({
  size,
  primaryRowSwitch,
  setPrimaryRowSwitch,
  rowName,
  updateColumnName,
  setIntensityUnit,
  setRestPerRepUnit,
  setPaceUnit,
  paceUnit,
  name,
  unit,
  visible,
}) => {
  const { colors, isDark } = useTheme();
  /** Light: the current option is the grey row. Dark flips it - the current
   * option is white and the rest are grey. Group headers (Running/Rowing) and
   * rows that can't be picked stay grey in both; they're bold, so they still
   * read as headers. */
  const optionTextColor = (isOption, selected) => {
    if (!isOption) return colors.textSecondary;
    if (isDark) return selected ? colors.textStrong : colors.textSecondary;
    return selected ? colors.textSecondary : colors.text;
  };

  const menuConfig = [
    {
      condition: rowName === 'pace',
      items: PACE_MENU_ITEMS.map((paceName) => ({
        label: formatPaceLabel(paceName),
        value: paceName,
        isSelectable: paceName !== 'Running' && paceName !== 'Rowing',
        selected: paceUnit === paceName,
        action: () => setPaceUnit(paceName),
      })),
    },
    {
      condition: ['intensity (bpm)', 'intensity (RPE)', 'intensity (%)', 'intensity (W)'].includes(rowName),
      items: [
        {
          label: 'Heart Rate (bpm)',
          intensityType: 'bpm',
          selected: primaryRowSwitch['intensity (bpm)'],
          isSelectable: true,
        },
        {
          label: 'RPE',
          intensityType: 'RPE',
          selected: primaryRowSwitch['intensity (RPE)'],
          isSelectable: true,
        },
        {
          label: 'Percentage (%)',
          intensityType: '%',
          selected: primaryRowSwitch['intensity (%)'],
          isSelectable: true,
        },
        {
          label: 'Watts (W)',
          intensityType: 'W',
          selected: primaryRowSwitch['intensity (W)'],
          isSelectable: true,
        },
      ].map(({
        label, intensityType, selected, isSelectable,
      }) => ({
        label,
        selected,
        isSelectable,
        action: () => {
          setIntensityUnit(intensityType);
          updateColumnName(rowName, `intensity (${intensityType})`);
        },
      })),
    },
    {
      condition: ['rest per rep (secs)', 'rest per rep (bpm)'].includes(rowName),
      items: [
        {
          label: 'Seconds (s)',
          restUnitType: 'secs',
          selected: primaryRowSwitch['rest per rep (secs)'],
          isSelectable: true,
        },
        {
          label: 'Heart Rate (bpm)',
          restUnitType: 'bpm',
          selected: primaryRowSwitch['rest per rep (bpm)'],
          isSelectable: true,
        },
      ].map(({
        label, restUnitType, selected, isSelectable,
      }) => ({
        label,
        selected,
        isSelectable,
        action: () => {
          setRestPerRepUnit(restUnitType);
          updateColumnName(rowName, `rest per rep (${restUnitType})`);
        },
      })),
    },
  ];

  // Get the format items based on the condition
  const formatItems = menuConfig
    .filter((config) => config.condition)
    .flatMap((config) => config.items);

  const isDeleteOnly = rowName === 'prescribed value' || rowName === 'rest' || rowName === 'reps';

  const deleteAction = () => {
    const flippedStatus = !primaryRowSwitch[rowName];
    setPrimaryRowSwitch({ ...primaryRowSwitch, [rowName]: flippedStatus });
  };

  /** Controlled open + keyboard navigation (arrow/Enter/Escape), since
   * rc-menu doesn't take focus on open. Navigable items are the
   * selectable format options plus the Delete row. */
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  /** Mouse hover, tracked alongside the keyboard cursor so either one fills
   * the whole row. 'delete' marks the Delete row. */
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const navItems = isDeleteOnly
    ? [{ action: deleteAction }]
    : [...formatItems.filter((i) => i.isSelectable), { action: deleteAction }];

  /** Open on the current value, so the highlighted row is the checked one. */
  useEffect(() => {
    if (open) {
      const selectedIndex = navItems.findIndex((i) => i.selected);
      setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    } else {
      setHoveredIndex(null);
    }
  }, [open]);

  /** Close when the calendar scrolls, like the calendar's other popups - the
   * menu is portaled, so it would otherwise float away from its row. Capture
   * phase because the calendar list scrolls in its own container. Scrolls
   * inside the menu itself (`.rc-dropdown`, the portaled overlay) don't count:
   * the pace list is long enough to scroll. */
  useEffect(() => {
    if (!open) return undefined;
    const handleScroll = (e) => {
      if (e.target?.closest?.('.rc-dropdown')) return;
      setOpen(false);
    };
    document.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', handleScroll, { capture: true });
  }, [open]);

  /** Own the arrow keys while open, so useCalendarKeyboardScroll leaves the
   * calendar where it is instead of sliding a row out from under the menu. */
  useEffect(() => {
    if (!open) return undefined;
    return claimArrowKeys();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((p) => Math.min(p + 1, navItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((p) => Math.max(p - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = navItems[focusedIndex];
        if (item) { item.action(); setOpen(false); }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, focusedIndex, navItems]);

  const menu = (
    <Menu
      style={{
        padding: 8,
        width: 229,
        overflowY: 'auto',
        marginBottom: '0px',
        border: `1px solid ${colors.borderLight}`,
        gap: 8,
        background: colors.surfaceElevated,
        maxHeight: formatItems.length > 10 ? 300 : 'auto',
        scrollbarWidth: 'thin',
      }}
    >
      <MenuWrapper>
        {(() => {
          let navIndex = -1;
          return formatItems.map(({
            label, value, action, selected, isSelectable,
          }, index) => {
            if (isSelectable) navIndex += 1;
            const rowNavIndex = navIndex;
            const isFocused = isSelectable && rowNavIndex === focusedIndex;
            const isHovered = isSelectable && rowNavIndex === hoveredIndex;
            return (
              <MenuItem
                key={value || label || index}
                style={{
                  fontWeight: (value === 'Running' || value === 'Rowing') && 700,
                  paddingLeft: (rowName === 'pace' && value !== 'Running' && value !== 'Rowing') && 12,
                  paddingRight: (rowName === 'pace' && value !== 'Running' && value !== 'Rowing') && 12,
                  cursor: (isSelectable && value !== 'Running' && value !== 'Rowing') ? 'pointer' : 'default',
                  color: (isSelectable && value !== 'Running' && value !== 'Rowing') ? colors.text : colors.textSecondary,
                  background: (isFocused || isHovered)
                    ? colors.surfaceSecondary
                    : colors.surfaceElevated,
                }}
                onMouseEnter={isSelectable ? () => setHoveredIndex(rowNavIndex) : undefined}
                onMouseLeave={isSelectable ? () => setHoveredIndex(null) : undefined}
            /** Close on selection like the keyboard Enter path — with a
             * controlled `visible` rc-dropdown won't close itself. */
                onClick={isSelectable ? () => { action(); setOpen(false); } : undefined}
              >
                <IconContainer>
                  <ArrowDiv>
                    <MenuText color={optionTextColor(isSelectable && value !== 'Running' && value !== 'Rowing', selected)}>{label}</MenuText>
                    {selected && isSelectable && (
                    <IcomoonReact iconSet={iconSet} size={12} icon='checkmark' color={isDark ? colors.textStrong : colors.textSecondary} />
                    )}
                  </ArrowDiv>
                </IconContainer>
              </MenuItem>
            );
          });
        })()}
        {formatItems.length > 0 && <CustomDivider />}
        <MenuItem
          style={{
            cursor: 'pointer',
            display: 'flex',
            width: '100%',
            background: (focusedIndex === navItems.length - 1 || hoveredIndex === 'delete')
              ? colors.surfaceSecondary
              : colors.surfaceElevated,
          }}
          onMouseEnter={() => setHoveredIndex('delete')}
          onMouseLeave={() => setHoveredIndex(null)}
          onClick={() => { deleteAction(); setOpen(false); }}
        >
          <IconContainer>
            <IcomoonReact
              iconSet={iconSet}
              size={17}
              icon='trashcan'
              color='#FF0505'
              style={{ marginRight: 5 }}
            />
            <ArrowDiv>
              <MenuText>Delete</MenuText>
            </ArrowDiv>
          </IconContainer>
        </MenuItem>
      </MenuWrapper>
    </Menu>
  );

  const deleteOnlyMenu = (
    <Menu
      style={{
        width: 229,
        overflowY: 'auto',
        marginBottom: '0px',
        border: `1px solid ${colors.borderLight}`,
        background: colors.surfaceElevated,
      }}
    >
      <MenuWrapper>
        <MenuItem
          style={{
            padding: 8,
            cursor: 'pointer',
            background: (focusedIndex === 0 || hoveredIndex === 'delete') ? colors.surfaceSecondary : colors.surfaceElevated,
          }}
          onMouseEnter={() => setHoveredIndex('delete')}
          onMouseLeave={() => setHoveredIndex(null)}
          onClick={() => { deleteAction(); setOpen(false); }}
        >
          <DeleteIconContainer>
            <IcomoonReact
              iconSet={iconSet}
              size={17}
              icon='trashcan'
              color='#FF0505'
              style={{ marginRight: 5 }}
            />
            <ArrowDiv>
              <MenuText>Delete</MenuText>
            </ArrowDiv>
          </DeleteIconContainer>
        </MenuItem>
      </MenuWrapper>
    </Menu>
  );

  return (
    <Dropdown
      trigger={visible ? ['click'] : []} /** Disable trigger if not visible */
      visible={open}
      onVisibleChange={setOpen}
      overlay={isDeleteOnly ? deleteOnlyMenu : menu}
      /** rc-dropdown's stylesheet defaults the overlay to z-index 1070, which
       * sits *below* BaseModal's 1100 — the menu would render behind the modal
       * and read as an unclickable caret. Matches SwapPropDropdown. */
      overlayStyle={{ zIndex: 1101 }}
      aria-haspopup='true'
    >
      <DropdownContainerPrimary
        onClick={(e) => e.stopPropagation()}
        visible={visible}
        {...(visible ? {
          role: 'button',
          tabIndex: 0,
          onKeyDown: (e) => {
            /** An open menu owns the keyboard. Focus can still sit on this trigger
             * after the coach mouse-opens another row's menu, and without this
             * the same ArrowDown would open this one too, both menus then
             * arrowing in lockstep. */
            if (hasArrowKeyOwner()) return;
            if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
              e.preventDefault();
              e.stopPropagation();
              setOpen(true);
            }
          },
        } : {})}
      >
        <DropdownIcon padding={size}>
          {name}
          {' '}
          {unit || null}
          {!visible ? null : (
            <IcomoonReact
              iconSet={iconSet}
              size={7}
              style={{ minWidth: '7px' }}
              icon='down-arrow'
              color={colors.text}
            />
          )}
        </DropdownIcon>
      </DropdownContainerPrimary>
    </Dropdown>
  );
};

SwapPropDropdownSAQC.propTypes = {
  size: PropTypes.number.isRequired,
  primaryRowSwitch: PropTypes.instanceOf(Object).isRequired,
  setPrimaryRowSwitch: PropTypes.func.isRequired,
  rowName: PropTypes.string.isRequired,
  updateColumnName: PropTypes.func.isRequired,
  setIntensityUnit: PropTypes.func.isRequired,
  setRestPerRepUnit: PropTypes.func.isRequired,
  setPaceUnit: PropTypes.func.isRequired,
  paceUnit: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  unit: PropTypes.string.isRequired,
  visible: PropTypes.bool,
};

SwapPropDropdownSAQC.defaultProps = {
  visible: true,
};

export default SwapPropDropdownSAQC;
